// Same row shape, two API surfaces: `/api/v1/work-orders` (gated by
// `work_orders:*` perms) and `/api/v1/tasks` (gated by `tasks:*` perms).
// The two namespaces are otherwise identical so we factory both out of
// the same builder — pass the prefix to swap the URL family.
function makeWorkOrdersLikeApi(client, prefix) {
    return {
        list: (params) => client.get(prefix, { params }).then((r) => r.data),
        get: (id) => client.get(`${prefix}/${id}`).then((r) => r.data),
        create: (body) => client.post(prefix, body).then((r) => r.data),
        update: (id, body) => client.patch(`${prefix}/${id}`, body).then((r) => r.data),
        remove: (id) => client.delete(`${prefix}/${id}`).then((r) => r.data),
        /// Soft-delete up to 200 work orders in one call.
        bulkRemove: (ids) => client.post(`${prefix}/bulk-delete`, { ids }).then((r) => r.data),
        // Transitions
        assign: (id, body) => client.post(`${prefix}/${id}/assign`, body).then((r) => r.data),
        execute: (id) => client.post(`${prefix}/${id}/execute`).then((r) => r.data),
        close: (id, body) => client.post(`${prefix}/${id}/close`, body ?? {}).then((r) => r.data),
        acknowledge: (id, body) => client.post(`${prefix}/${id}/acknowledge`, body ?? {}).then((r) => r.data),
        reject: (id, notes) => client.post(`${prefix}/${id}/reject`, { notes }).then((r) => r.data),
        reopen: (id) => client.post(`${prefix}/${id}/reopen`).then((r) => r.data),
        // Recurrence
        stopRecurrence: (id) => client.post(`${prefix}/${id}/stop-recurrence`).then((r) => r.data),
        // Labor time
        logTime: (id, minutes) => client.post(`${prefix}/${id}/log-time`, { minutes }).then((r) => r.data),
        getRecurrenceChain: (id) => client.get(`${prefix}/${id}/recurrence-chain`).then((r) => r.data),
        // Approval steps
        listApprovalSteps: (id) => client.get(`${prefix}/${id}/approval-steps`).then((r) => r.data),
        addApprovalStep: (id, body) => client.post(`${prefix}/${id}/approval-steps`, body).then((r) => r.data),
        removeApprovalStep: (id, stepId) => client.delete(`${prefix}/${id}/approval-steps/${stepId}`).then((r) => r.data),
        // Parts
        listParts: (id) => client.get(`${prefix}/${id}/parts`).then((r) => r.data),
        addPart: (id, body) => client.post(`${prefix}/${id}/parts`, body).then((r) => r.data),
        removePart: (id, partId) => client.delete(`${prefix}/${id}/parts/${partId}`).then((r) => r.data),
        // Attachments
        listAttachments: (id) => client.get(`${prefix}/${id}/attachments`).then((r) => r.data),
        uploadAttachment: (id, file, onProgress) => {
            const form = new FormData();
            form.append('file', file);
            return client.post(`${prefix}/${id}/attachments`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => { if (onProgress && e.total)
                    onProgress(Math.round((e.loaded / e.total) * 100)); },
            }).then((r) => r.data);
        },
        viewAttachment: (id, fileId) => client.get(`${prefix}/${id}/attachments/${fileId}/view`, { responseType: 'blob' }).then((r) => r),
        deleteAttachment: (id, fileId) => client.delete(`${prefix}/${id}/attachments/${fileId}`).then((r) => r.data),
        // Linked checklists (multi). Each link can have a window:
        //   - scheduled_at: earliest start (tech can't begin before this)
        listLinkedChecklists: (id) => client.get(`${prefix}/${id}/linked-checklists`).then((r) => r.data),
        addLinkedChecklist: (id, template_id) => client.post(`${prefix}/${id}/linked-checklists`, { template_id })
            .then((r) => r.data),
        removeLinkedChecklist: (id, templateId) => client.delete(`${prefix}/${id}/linked-checklists/${templateId}`).then((r) => r.data),
        // Report — omit `templateId` to get the org's default template (or
        // the hardcoded built-in layout if no default is set); pass one to
        // force a specific template, as Settings → Data Export's picker does.
        getReportHtml: (id, templateId) => client.get(`${prefix}/${id}/report`, {
            params: templateId ? { template_id: templateId } : undefined,
            responseType: 'text',
            transformResponse: [(d) => d],
        }).then((r) => r.data),
        /// Real, server-rendered PDF bytes (a headless-Chromium print, not a
        /// client-side screenshot) — see api/src/lib/htmlToPdf.js for why.
        /// Overrides the client's default 30s timeout: rendering waits on the
        /// report's own <img> tags loading (checklist photos, attachments,
        /// signatures — each its own same-origin proxy round trip), which a
        /// report with several executed checklists' worth of photos can push
        /// past 30s even though the render itself is healthy and will finish.
        /// Kept comfortably above htmlToPdf.js's own 60s render timeout so a
        /// slow-but-successful render isn't cut off client-side first.
        downloadReportPdf: (id, templateId) => client.get(`${prefix}/${id}/report`, {
            params: { format: 'pdf', ...(templateId ? { template_id: templateId } : {}) },
            responseType: 'arraybuffer',
            timeout: 120_000,
        }).then((r) => r.data),
        // Comments
        listComments: (id) => client.get(`${prefix}/${id}/comments`).then((r) => r.data),
        addComment: (id, text) => client.post(`${prefix}/${id}/comments`, { text }).then((r) => r.data),
        deleteComment: (id, commentId) => client.delete(`${prefix}/${id}/comments/${commentId}`).then((r) => r.data),
        /** Multipart upload — attaches a file/image/audio to a new comment. */
        uploadCommentAttachment: (id, file, opts = {}, onProgress) => {
            const form = new FormData();
            form.append('file', file, file.name ?? 'attachment');
            const url = `${prefix}/${id}/comments/upload`
                + (opts.caption ? `?caption=${encodeURIComponent(opts.caption)}` : '');
            return client.post(url, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (onProgress && e.total)
                        onProgress(Math.round((e.loaded / e.total) * 100));
                },
            }).then((r) => r.data);
        },
        /** Browser-friendly URL for an attachment. */
        commentAttachmentUrl: (id, commentId) => `${prefix}/${id}/comments/${commentId}/attachment`,
        /** Fetch a comment attachment as a Blob (for image/audio inline rendering). */
        viewCommentAttachment: (id, commentId) => client.get(`${prefix}/${id}/comments/${commentId}/attachment`, { responseType: 'blob' }).then((r) => r),
        // ── Digital signatures (close / acknowledge gate) ──
        /**
         * Upload a signature image (drawn-on-canvas blob or chosen file) for
         * later use as a close or acknowledge gate. Returns the signature
         * row including its id, which the close/acknowledge call must echo
         * back in `signature_id`.
         */
        uploadSignature: (id, file, action, onProgress) => {
            const form = new FormData();
            form.append('action', action);
            form.append('file', file, file.name ?? 'signature.png');
            return client.post(`${prefix}/${id}/signatures`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (onProgress && e.total)
                        onProgress(Math.round((e.loaded / e.total) * 100));
                },
            }).then((r) => r.data);
        },
        listSignatures: (id) => client.get(`${prefix}/${id}/signatures`).then((r) => r.data),
    };
}
export const workOrdersApi = (client) => makeWorkOrdersLikeApi(client, '/api/v1/work-orders');
// Tasks reuse the entire WO surface — same payload shape, same routes
// per-id, gated by the `tasks:*` permission family on the server.
export const tasksApi = (client) => makeWorkOrdersLikeApi(client, '/api/v1/tasks');
//# sourceMappingURL=work-orders.js.map